import moment from 'moment'
import cn from 'classnames'
import { useHistory } from 'react-router'
import React, { useMemo } from 'react'
import { Field, Form, Formik } from 'formik'
import { DateSchema } from 'yup'
import { RRuleSet, rrulestr } from 'rrule'

import { useFeatureEnabled } from 'core/feature/hooks'
import yup from 'civic-champs-shared/utils/yup'
import { EventSequenceInfo, RecurrenceInfo } from 'Event/interfaces/interfaceCreateEditEvent'
import useChangeEventScheduling from '../../../../hooks/useChangeEventScheduling'
import { StyledKeyboardDatePicker } from '../../../../../civic-champs-shared/formik/components/StyledFormikDateTimePickers'
import { FormModal, FormModalScreen } from '../../../../../components/FormModal'
import { useAddEditEventStyles } from '../../../../hooks/useAddEditEventStyles'
import RadioGroupFormField from '../../../../../components/RadioGroupFormField'
import DatePickerMultiSelect from '../../../../components/date-picker-multi-select-new'
import dayjs from '../../../../../civic-champs-shared/core/utils/dayjs'

interface Props {
  complete: () => void
  close: () => void
  cancel: () => void
  showing: boolean
  recurrenceInfo: RecurrenceInfo
  sequenceInfo?: EventSequenceInfo
  encodedOccurrence: string | number
}

//TODO add rdate and dates validation for pattern editing
const useValidationSchema = (isStartDateDisabled: boolean) =>
  useMemo(
    () =>
      yup.object({
        startDate: yup.date().when('changePattern', {
          is: false,
          then: (schema: DateSchema) => {
            if (isStartDateDisabled) return schema.nullable().notRequired()

            return schema
              .test('today-or-later', 'The start time must be after today', function (value: any) {
                return value && moment(value).isAfter(new Date(), 'day')
              })
              .required()
          },
          otherwise: (schema: DateSchema) => schema.nullable().notRequired(),
        }),
        endDate: yup.date().when('changePattern', {
          is: false,
          then: (schema: DateSchema) => {
            return schema
              .test('min-by-date', 'The end time cannot occur before the start time', function (value: any) {
                return value && moment(value).isSameOrAfter(moment(this.parent.startDate), 'day')
              })
              .required()
          },
          otherwise: (schema: DateSchema) => schema.nullable().notRequired(),
        }),
      }),
    [isStartDateDisabled],
  )

//TODO what tz should this be operating in?  Do we need to make translations?
export const ChangeSchedulingModal = (props: Props) => {
  const { close, complete, showing, recurrenceInfo, sequenceInfo, encodedOccurrence } = props

  const customDatesEnabled = useFeatureEnabled('CustomDateSequenceEditing')

  let { endDate, startDate, pattern } = recurrenceInfo
  if (sequenceInfo) {
    startDate = sequenceInfo.startsAt
    endDate = sequenceInfo.endsAt
    pattern = sequenceInfo.computedPattern
  }

  const startDateDisabled = useMemo(() => moment(startDate).isBefore(new Date(), 'day'), [startDate])
  const dates = rrulestr(pattern as string).all()
  const changeEventScheduling = useChangeEventScheduling()
  const history = useHistory()

  const validationSchema = useValidationSchema(startDateDisabled)

  const classes = useAddEditEventStyles()
  const handleSubmit = async (values: { startDate: Date; endDate: Date; dates: Date[]; changePattern: boolean }) => {
    let payload
    if (values.changePattern) {
      payload = {
        recurrencePattern: values.dates
          .reduce((rset, date) => {
            rset.rdate(date)
            return rset
          }, new RRuleSet())
          .toString(),
      }
    } else if (startDateDisabled) {
      payload = { endDate: values.endDate.toISOString() }
    } else {
      //TODO check if this breaks if we don't touch the start date value?
      payload = {
        startDate: moment(values.startDate).toISOString(),
        endDate: moment(values.endDate).toISOString(),
      }
    }
    await changeEventScheduling(encodedOccurrence as string, payload)
    complete()
    history.push('/list-of-events')
  }
  return (
    <FormModal id="event-scheduling-prompt" showing={showing} close={close} title="Edit Event Scheduling">
      <Formik
        initialValues={{ startDate, endDate, dates, changePattern: false }}
        onSubmit={values => handleSubmit(values)}
        validationSchema={validationSchema}
        validateOnChange
      >
        {({ submitForm, isSubmitting, values, setFieldValue, errors }) => {
          const handleChangeMultiSelectDates = (date: string) => {
            const dateObject = dayjs(date, 'MM/DD/YYYY')
            if (dateObject.isBefore(new Date(), 'day')) {
              return
            }
            const dates = (values.dates || []).map(v => dayjs(v))
            const index = dates.findIndex(v => v.isSame(dateObject, 'day'))
            if (index !== -1) {
              setFieldValue(
                'dates',
                dates.filter((v, i) => i !== index).map(v => v.toDate()),
              )
            } else {
              setFieldValue(
                'dates',
                [...dates, dateObject].sort((a, b) => a.diff(b, 'day')).map(v => v.toDate()),
              )
            }
          }

          return (
            <Form>
              <FormModalScreen
                onBack={close}
                onNext={submitForm}
                nextText="Save"
                backText="Cancel"
                disabled={isSubmitting}
              >
                <div className={cn(classes.section, classes.subSection)}>
                  {customDatesEnabled ? (
                    <>
                      <RadioGroupFormField
                        valueIsBoolean
                        name="changePattern"
                        disabled={isSubmitting}
                        options={[
                          {
                            label: 'Adjust Start and End Date',
                            value: false,
                          },
                        ]}
                      />
                      {!values.changePattern && (
                        <>
                          <div className={classes.dateTimePickers}>
                            <Field
                              inputVariant="outlined"
                              label="Start Date *"
                              higher
                              fullWidth
                              component={StyledKeyboardDatePicker}
                              name="startDate"
                              disabled={startDateDisabled || isSubmitting}
                            />
                          </div>

                          <div className={classes.dateTimePickers}>
                            <Field
                              higher
                              fullWidth
                              inputVariant="outlined"
                              label="End Date *"
                              component={StyledKeyboardDatePicker}
                              name="endDate"
                              minDate={moment.max(moment(values.startDate), moment()).startOf('day').toDate()}
                              minDateMessage={'The end date cannot occur before the start date.'}
                            />
                          </div>
                        </>
                      )}
                      <RadioGroupFormField
                        valueIsBoolean
                        name="changePattern"
                        disabled={isSubmitting}
                        options={[
                          {
                            label: 'Add/Remove Custom Dates',
                            value: true,
                          },
                        ]}
                      />
                      {values.changePattern && (
                        <div className={classes.dateTimePickers}>
                          <DatePickerMultiSelect
                            label="Event Dates"
                            placeholder="Select Event Dates"
                            datesOnScreen={4}
                            dates={values.dates as Date[]}
                            onChange={handleChangeMultiSelectDates}
                            error={errors.dates as string}
                            belowField={true}
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <p>Adjust Start and End Date</p>
                      <div className={classes.dateTimePickers}>
                        <Field
                          inputVariant="outlined"
                          label="Start Date *"
                          higher
                          fullWidth
                          component={StyledKeyboardDatePicker}
                          name="startDate"
                          disabled={startDateDisabled || isSubmitting}
                        />
                      </div>

                      <div className={classes.dateTimePickers}>
                        <Field
                          higher
                          fullWidth
                          inputVariant="outlined"
                          label="End Date *"
                          component={StyledKeyboardDatePicker}
                          name="endDate"
                          minDate={moment.max(moment(values.startDate), moment()).startOf('day').toDate()}
                          minDateMessage={'The end date cannot occur before the start date.'}
                        />
                      </div>
                    </>
                  )}
                </div>
              </FormModalScreen>
            </Form>
          )
        }}
      </Formik>
    </FormModal>
  )
}
